import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  Typography,
  Box,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  AccountManagementComment,
  AccountManagementCommentObjectType,
  AccountManagementTask,
  AccountManagementObjectType,
  AccountManagementTaskStatus,
  AccountManagementTargetUserRole,
} from "../../../models/AccountManagement";
import {
  useRescheduleAccountManagementTask,
  useCancelAccountManagementTask,
} from "../../../hooks/useAccountManagement";
import RescheduleTaskDialog from "./RescheduleTaskDialog";

interface AccountManagementTaskTableProps {
  tasks: AccountManagementTask[];
  /**
   * All AM comments of the project. Used to compute the dual "Terminé/À faire"
   * sub-status of the Bilan row (one per role: Client / Talent). Empty array
   * is a safe fallback that renders the row as "all to-do".
   */
  comments: AccountManagementComment[];
  projectId: number;
  isLead: boolean;
}

// i18n key suffix for each task object type, looked up under `accountManagement.objectTypes`.
const OBJECT_TYPE_I18N_KEYS: Record<AccountManagementObjectType, string> = {
  [AccountManagementObjectType.CheckList]: "checkList",
  [AccountManagementObjectType.KickOff]: "kickOff",
  [AccountManagementObjectType.Feedback]: "feedback",
  [AccountManagementObjectType.PreBilanUpsell]: "preBilanUpsell",
  [AccountManagementObjectType.Satisfaction]: "satisfaction",
  [AccountManagementObjectType.Bilan]: "bilan",
  [AccountManagementObjectType.QuarterlyReview]: "quarterlyReview",
};

const getObjectTypeLabel = (
  type: AccountManagementObjectType,
  t: TFunction,
  sequenceNumber?: number
): string => {
  if (type === AccountManagementObjectType.QuarterlyReview && sequenceNumber) {
    return t("accountManagement.objectTypes.quarterlyReviewWithSeq", { seq: sequenceNumber });
  }
  const key = OBJECT_TYPE_I18N_KEYS[type];
  return key ? t(`accountManagement.objectTypes.${key}`) : String(type);
};

const getStatusColor = (status: AccountManagementTaskStatus): "default" | "primary" | "success" | "warning" | "error" | "info" => {
  switch (status) {
    case AccountManagementTaskStatus.Planned:
      return "default";
    case AccountManagementTaskStatus.Sent:
      return "info";
    case AccountManagementTaskStatus.Completed:
      return "success";
    case AccountManagementTaskStatus.ToModerate:
      return "warning";
    case AccountManagementTaskStatus.Published:
      return "success";
    case AccountManagementTaskStatus.Rejected:
      return "error";
    case AccountManagementTaskStatus.Cancelled:
      return "default";
    default:
      return "default";
  }
};

/**
 * Per-status visual override applied on top of the MUI `color` palette. PO design:
 *   - "Envoyée" → custom soft-blue background + dark-grey text (more readable
 *     than the default MUI `info` chip).
 *   - "Terminée" / "Publiée" → keep the MUI `success` green background but use
 *     a near-white text color (#F3F3F3) for better contrast.
 * Other statuses fall back to the MUI defaults from getStatusColor.
 */
const getStatusChipOverrideSx = (
  status: AccountManagementTaskStatus
): { backgroundColor?: string; color?: string } | undefined => {
  switch (status) {
    case AccountManagementTaskStatus.Sent:
      return { backgroundColor: "#E5F6FF", color: "#242C35" };
    case AccountManagementTaskStatus.Completed:
    case AccountManagementTaskStatus.Published:
      return { color: "#F3F3F3" };
    default:
      return undefined;
  }
};

// PO design tokens for the recipient-role chips.
const getRoleChipColor = (role: AccountManagementTargetUserRole): { bg: string; text: string } => {
  switch (role) {
    case AccountManagementTargetUserRole.Client:
      return { bg: "#FFF8DC", text: "#382325" };
    case AccountManagementTargetUserRole.Freelancer:
      return { bg: "#FDF4F2", text: "#FF4300" };
    case AccountManagementTargetUserRole.CSM:
      return { bg: "#EFEEFA", text: "#4836bf" };
    default:
      return { bg: "#F5F5F5", text: "#616161" };
  }
};

const STATUS_I18N_KEYS: Record<AccountManagementTaskStatus, string> = {
  [AccountManagementTaskStatus.Planned]: "planned",
  [AccountManagementTaskStatus.Sent]: "sent",
  [AccountManagementTaskStatus.Completed]: "completed",
  [AccountManagementTaskStatus.ToModerate]: "toModerate",
  [AccountManagementTaskStatus.Published]: "published",
  [AccountManagementTaskStatus.Rejected]: "rejected",
  [AccountManagementTaskStatus.Cancelled]: "cancelled",
};

const getStatusLabel = (status: AccountManagementTaskStatus, t: TFunction): string => {
  const key = STATUS_I18N_KEYS[status];
  return key ? t(`accountManagement.statuses.${key}`) : String(status);
};

const ROLE_I18N_KEYS: Record<AccountManagementTargetUserRole, string> = {
  [AccountManagementTargetUserRole.Client]: "client",
  [AccountManagementTargetUserRole.Freelancer]: "freelancer",
  [AccountManagementTargetUserRole.CSM]: "csm",
};

const getRoleLabel = (role: AccountManagementTargetUserRole, t: TFunction): string => {
  const key = ROLE_I18N_KEYS[role];
  return key ? t(`accountManagement.roles.${key}`) : String(role);
};

/**
 * Structured description shown in the "Tâche" column. Always has a `mainText`
 * (one-liner like "Envoi du message automatisé.") and may have an indented
 * sub-bullet list — optionally introduced by a header line ("Le CSM:").
 */
type TaskDisplay = {
  mainText: string;
  intro?: string;
  bullets?: string[];
};

const getTaskDescription = (task: AccountManagementTask, t: TFunction): TaskDisplay => {
  if (task.isFullyAutomated) {
    return { mainText: t("accountManagement.automatedTask") };
  }

  const display: TaskDisplay = { mainText: t("accountManagement.automatedMessageSent") };

  switch (task.objectType) {
    case AccountManagementObjectType.Feedback:
    case AccountManagementObjectType.PreBilanUpsell:
    case AccountManagementObjectType.Bilan:
    case AccountManagementObjectType.QuarterlyReview:
      display.intro = t("accountManagement.csmIntroLine");
      display.bullets = [
        t("accountManagement.csmOrganizesExchange"),
        t("accountManagement.contributeComment"),
      ];
      break;
    case AccountManagementObjectType.Satisfaction:
      // Single bullet sentence, no "Le CSM:" header — the sentence already
      // identifies the actor (Client / Talent).
      if (task.targetUserRole === AccountManagementTargetUserRole.Client) {
        display.bullets = [t("accountManagement.clientContributeEvaluation")];
      } else if (task.targetUserRole === AccountManagementTargetUserRole.Freelancer) {
        display.bullets = [t("accountManagement.talentContributeEvaluation")];
      }
      break;
    default:
      break;
  }

  return display;
};

const isTaskCompleted = (task: AccountManagementTask): boolean => {
  return [
    AccountManagementTaskStatus.Completed,
    AccountManagementTaskStatus.Published,
    AccountManagementTaskStatus.ToModerate,
  ].includes(task.status);
};

/**
 * Per-role sub-statuses for a Bilan task — used by the Status cell to show TWO
 * chips ("Terminé/À faire (Client)" + "(Talent)") instead of the single chip
 * for other task types.
 *
 * The Bilan task itself is unique (addressed to the CSM Lead), but it represents
 * two sub-deliverables: a comment on the Client side AND a comment on the Talent
 * side. The task is only marked Completed by the backend when BOTH comments
 * exist — see AccountManagementService.TryCompleteBilanTaskAsync.
 *
 * `clientDone` / `talentDone` here mirror that: a sub-status is `done` when at
 * least one corresponding comment exists on the project, `todo` otherwise.
 */
const getBilanSubStatuses = (
  comments: AccountManagementComment[]
): { clientDone: boolean; talentDone: boolean } => ({
  clientDone: comments.some(
    (c) => c.objectType === AccountManagementCommentObjectType.BilanClient
  ),
  talentDone: comments.some(
    (c) => c.objectType === AccountManagementCommentObjectType.BilanTalent
  ),
});

// Action permissions per task status — kept here so the UI matches the backend
// validation in AccountManagementService.RescheduleTaskAsync / CancelTaskAsync.
const canRescheduleTask = (task: AccountManagementTask): boolean =>
  task.status === AccountManagementTaskStatus.Planned;

const canCancelTask = (task: AccountManagementTask): boolean =>
  task.status !== AccountManagementTaskStatus.Completed &&
  task.status !== AccountManagementTaskStatus.Published &&
  task.status !== AccountManagementTaskStatus.Cancelled;

/**
 * Stable group key for the rowSpan logic and the sort: tasks sharing the same
 * key are merged in the "Objet" column. We include sequenceNumber so each
 * QuarterlyReview iteration stays its own group.
 */
const taskGroupKey = (t: AccountManagementTask): string =>
  `${t.objectType}:${t.sequenceNumber ?? ""}`;

/**
 * Reorders the tasks so that all rows sharing the same (objectType, sequenceNumber)
 * are consecutive in the array — which is the precondition for the column-merge
 * rowSpan logic to work. Without this, the API's natural order (by date) can
 * scatter rows of the same object across the table and the "Objet" cell only
 * merges sub-stretches at best.
 *
 * Order:
 *   1. Primary: by objectType + sequenceNumber. Groups are then ranked by the
 *      earliest scheduledDate they contain — keeps the table chronologically
 *      readable from top to bottom.
 *   2. Within a group: by scheduledDate ascending, then by targetUserId for
 *      a deterministic order (avoids React key churn on re-render).
 */
const sortTasksByObjectThenDate = (
  tasks: AccountManagementTask[]
): AccountManagementTask[] => {
  if (tasks.length <= 1) return tasks;

  const groups = new Map<string, AccountManagementTask[]>();
  for (const task of tasks) {
    const key = taskGroupKey(task);
    const bucket = groups.get(key);
    if (bucket) bucket.push(task);
    else groups.set(key, [task]);
  }

  // Stable in-group sort: date asc, then user id asc.
  for (const bucket of groups.values()) {
    bucket.sort((a, b) => {
      const dateCmp =
        new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();
      if (dateCmp !== 0) return dateCmp;
      return a.targetUserId - b.targetUserId;
    });
  }

  // Rank groups by their earliest task date so the table still flows chronologically.
  const orderedGroups = Array.from(groups.values()).sort((a, b) => {
    const aDate = new Date(a[0].scheduledDate).getTime();
    const bDate = new Date(b[0].scheduledDate).getTime();
    if (aDate !== bDate) return aDate - bDate;
    // Tie-break on the group key for full determinism (same date across two
    // different objects shouldn't shuffle on re-render).
    return taskGroupKey(a[0]).localeCompare(taskGroupKey(b[0]));
  });

  return orderedGroups.flat();
};

/**
 * Returns, for each task index in the input array, the rowSpan to apply on the
 * "Objet" cell:
 *   - First task of a (objectType, sequenceNumber) run → rowSpan = run length
 *   - Subsequent tasks of the same run → rowSpan = 0 (caller skips the cell)
 *
 * Precondition: the input is already sorted so same-key tasks are consecutive
 * (use sortTasksByObjectThenDate above).
 */
const computeObjectColumnRowSpans = (tasks: AccountManagementTask[]): number[] => {
  const spans = new Array<number>(tasks.length).fill(0);
  let i = 0;
  while (i < tasks.length) {
    const key = taskGroupKey(tasks[i]);
    let j = i + 1;
    while (j < tasks.length && taskGroupKey(tasks[j]) === key) j++;
    spans[i] = j - i;
    i = j;
  }
  return spans;
};

const AccountManagementTaskTable: React.FC<AccountManagementTaskTableProps> = ({
  tasks,
  comments,
  projectId,
  isLead,
}) => {
  const { t, i18n } = useTranslation();
  const rescheduleTask = useRescheduleAccountManagementTask();
  const cancelTask = useCancelAccountManagementTask();

  // Per-row action menu state. Storing the task id (not just the anchor) lets
  // multiple rows coexist without flickering when the menu re-renders.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; taskId: number } | null>(null);
  const openTaskForMenu = menuAnchor
    ? tasks.find((task) => task.id === menuAnchor.taskId) ?? null
    : null;

  // Reschedule dialog — independent of the menu so the menu can close while
  // the dialog stays open.
  const [rescheduleTarget, setRescheduleTarget] = useState<AccountManagementTask | null>(null);

  // Reorder tasks so all rows of the same (objectType, sequenceNumber) are
  // consecutive — required for the "Objet" column rowSpan to merge correctly.
  // Without this, the API's date-based ordering scatters same-object rows.
  const { sortedTasks, objectColumnRowSpans } = useMemo(() => {
    const sorted = sortTasksByObjectThenDate(tasks);
    return {
      sortedTasks: sorted,
      objectColumnRowSpans: computeObjectColumnRowSpans(sorted),
    };
  }, [tasks]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, taskId: number) => {
    setMenuAnchor({ el: event.currentTarget, taskId });
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleClickReschedule = () => {
    if (openTaskForMenu) setRescheduleTarget(openTaskForMenu);
    handleCloseMenu();
  };

  const handleClickCancel = () => {
    if (openTaskForMenu && canCancelTask(openTaskForMenu)) {
      cancelTask.mutate({ projectId, taskId: openTaskForMenu.id });
    }
    handleCloseMenu();
  };

  const handleConfirmReschedule = (newDateIso: string) => {
    if (!rescheduleTarget) return;
    rescheduleTask.mutate(
      { projectId, taskId: rescheduleTarget.id, newDate: newDateIso },
      { onSuccess: () => setRescheduleTarget(null) }
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Show all tasks including Cancelled — those used to be filtered out, but the
  // CSM now cancels tasks explicitly and we want to keep the trace visible.
  return (
    <Paper
      elevation={3}
      sx={{
        overflow: "hidden",
        borderRadius: 2,
        "& .MuiTable-root": { borderCollapse: "collapse" },
      }}
    >
      <TableContainer sx={{ borderRadius: "inherit" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 160 }}>{t("accountManagement.object", "Objet")}</TableCell>
              <TableCell>{t("accountManagement.recipient", "Destinataire")}</TableCell>
              <TableCell sx={{ width: 110 }}>{t("accountManagement.date", "Date")}</TableCell>
              <TableCell sx={{ width: 120 }}>{t("accountManagement.status", "Statut")}</TableCell>
              {/* Narrower than before to give the table some breathing room — the
                  description is short and bullet-listed, no need to be wide. */}
              <TableCell sx={{ width: 280 }}>{t("accountManagement.task", "Tâche")}</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>
                {t("accountManagement.completed", "Réalisé")}
              </TableCell>
              {isLead && <TableCell align="center" sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTasks.map((task, index) => {
              const cancelled = task.status === AccountManagementTaskStatus.Cancelled;
              const showActionMenu = isLead && (canRescheduleTask(task) || canCancelTask(task));
              const description = getTaskDescription(task, t);
              const objectRowSpan = objectColumnRowSpans[index];
              return (
                <TableRow
                  key={task.id}
                  sx={{
                    opacity: cancelled ? 0.55 : 1,
                    "& td": cancelled
                      ? { textDecoration: "line-through", color: "text.disabled" }
                      : undefined,
                  }}
                >
                  {/* Object cell only rendered on the first row of each (objectType,
                      sequenceNumber) run; subsequent rows skip it so the cell
                      visually merges. */}
                  {objectRowSpan > 0 && (
                    <TableCell rowSpan={objectRowSpan} sx={{ verticalAlign: "top" }}>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {getObjectTypeLabel(task.objectType, t, task.sequenceNumber)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {task.targetUser.firstName} {task.targetUser.lastName}
                      </Typography>
                      <Chip
                        label={getRoleLabel(task.targetUserRole, t)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          backgroundColor: getRoleChipColor(task.targetUserRole).bg,
                          color: getRoleChipColor(task.targetUserRole).text,
                          border: "none",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.primary">
                      {formatDate(task.scheduledDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {task.objectType === AccountManagementObjectType.Bilan ? (
                      // Bilan: two sub-statuses (Client + Talent) instead of the single
                      // task status. The task itself is unique but covers two deliverables;
                      // see getBilanSubStatuses + AccountManagementService.TryCompleteBilanTaskAsync.
                      (() => {
                        const { clientDone, talentDone } = getBilanSubStatuses(comments);
                        const renderSubChip = (done: boolean, roleLabel: string) => (
                          <Chip
                            label={done
                              ? t("accountManagement.bilanSubStatusDone", "Terminé ({{role}})", { role: roleLabel })
                              : t("accountManagement.bilanSubStatusTodo", "À faire ({{role}})", { role: roleLabel })}
                            color={done ? "success" : "default"}
                            size="small"
                            variant={done ? "filled" : "outlined"}
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              ...(done ? { color: "#F3F3F3" } : undefined),
                            }}
                          />
                        );
                        return (
                          <Stack direction="column" spacing={0.5} alignItems="flex-start">
                            {renderSubChip(clientDone, t("accountManagement.roles.client"))}
                            {renderSubChip(talentDone, t("accountManagement.roles.freelancer"))}
                          </Stack>
                        );
                      })()
                    ) : (
                      <Chip
                        label={getStatusLabel(task.status, t)}
                        color={getStatusColor(task.status)}
                        size="small"
                        variant={task.status === AccountManagementTaskStatus.Planned ? "outlined" : "filled"}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          ...getStatusChipOverrideSx(task.status),
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {/* "Envoi du message automatisé." then optionally an intro
                        line ("Le CSM:") and an indented bullet list. */}
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.8rem", lineHeight: 1.6, color: "text.primary" }}
                    >
                      {description.mainText}
                    </Typography>
                    {description.intro && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.8rem", lineHeight: 1.6, color: "text.primary", mt: 0.25 }}
                      >
                        {description.intro}
                      </Typography>
                    )}
                    {description.bullets && description.bullets.length > 0 && (
                      <Box
                        component="ul"
                        sx={{ m: 0, mt: description.intro ? 0 : 0.25, pl: 2.5, listStyle: "disc" }}
                      >
                        {description.bullets.map((bullet, bIdx) => (
                          <Typography
                            key={bIdx}
                            component="li"
                            variant="body2"
                            sx={{ fontSize: "0.8rem", lineHeight: 1.6, color: "text.primary" }}
                          >
                            {bullet}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {/* Read-only checkbox: the "completed" state is driven by the
                        backend (a CSM Lead's comment marks the linked task as
                        Completed via TryCompleteRelatedTaskAsync). We never let
                        the CSM toggle it manually — that would let them mark a
                        task done without leaving any trace in the comments. */}
                    <Checkbox
                      checked={isTaskCompleted(task)}
                      disabled
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  {isLead && (
                    <TableCell align="center" sx={{ width: 56 }}>
                      {showActionMenu && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, task.id)}
                          aria-label={t("accountManagement.taskActions", "Actions")}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={isLead ? 7 : 6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {t("accountManagement.noTasks", "Aucune tâche planifiée")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action menu (one shared instance, anchored on whatever row was clicked) */}
      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {openTaskForMenu && canRescheduleTask(openTaskForMenu) && (
          <MenuItem onClick={handleClickReschedule}>
            <ListItemIcon>
              <EditCalendarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("accountManagement.rescheduleAction", "Modifier la date")}
            </ListItemText>
          </MenuItem>
        )}
        {openTaskForMenu && canCancelTask(openTaskForMenu) && (
          <MenuItem onClick={handleClickCancel} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              {t("accountManagement.cancelAction", "Annuler la tâche")}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Reschedule date picker dialog */}
      {rescheduleTarget && (
        <RescheduleTaskDialog
          open={Boolean(rescheduleTarget)}
          currentDate={rescheduleTarget.scheduledDate}
          isPending={rescheduleTask.isPending}
          onClose={() => setRescheduleTarget(null)}
          onConfirm={handleConfirmReschedule}
        />
      )}
    </Paper>
  );
};

export default AccountManagementTaskTable;
